/* tslint:disable:max-line-length */
import {
  CommentOutlined,
  DownOutlined,
  MailOutlined,
  QuestionCircleOutlined,
  SendOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useViewActions } from '@mm/state/actions/viewActions';

import WidgetModel, {
  EmailConfig,
  NavigateConfig,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';

import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as SmsIcon } from './phone.svg';
import classNames from 'classnames';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import { useCommonWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';

import { ITime } from '../../../../../../../../components/SlideoutMenu/menus/SlideoutDateTime/SlideoutDateTime';
import { Date } from '../elements/Date/Date';

import common from '../index.module.css';
import css from './Dialogue.module.css';
import { Count } from '../elements/Count/Count';
import { useOpenOnReady } from '../use-open-on-ready';
import { useDialoguePreviewMessage } from './use-dialogue-preview-message';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { uniqueId } from 'lodash';
import { ReviewBadge } from '../elements/ReviewBadge/ReviewBadge';
import { useAutomationActions } from '@mm/state/actions/automationActions';
import {
  EAutomationAction,
  IAutomationActionToOpenOnReady,
} from '@mm/state/model/AutomationModel';
import { PeopleIcon } from '@mm/icons/general/PeopleIcon';
import { MailIcon } from '@mm/icons/general/MailIcon';
import { ClickIcon } from '@mm/icons/general/ClickIcon';
import { ReactComponent as SOLDIcon } from 'assets/sold-icon.svg';
import { numFormatter } from '../../../../../../../../../common/helpers/string-helpers';
import automationStyles from '../../../../automationStyles.module.css';
import { Button, Heading, Tooltip } from '@mm/components';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useSendCapacityValue } from './use-send-capacity-value';
import { GraphOpts } from '../../use-graph';

type Props = { className?: string } & NodeProps<{
  widget: WidgetModel;
  opts: GraphOpts;
}>;
const VALID_PLATFORMS = ['sms', 'email'] as const;
export const DialogueIcon = CommentOutlined;

export const SMSDialogueIcon = SmsIcon;
export const EmailDialogueIcon = MailOutlined;

export const Dialogue: React.FC<Props> = ({
  className,
  data,
  isDragging,
  children,
}) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;

  const config = widget.config as NavigateConfig;

  const dialogueId =
    widget.type === WidgetTypes.NAVIGATE ? widget.config.pageId : widget.pageId;

  const page = useSelector(state =>
    pageEntityByIdSelector(state, dialogueId?.toString() || ''),
  );

  const [expandedAnalytics, setExpandedAnalytics] = useState(false);

  const platform = VALID_PLATFORMS.find(p => page?.platforms?.includes(p));
  const isEmail = platform === 'email';

  const sendCapacityValue = useSendCapacityValue(data?.opts?.campaignId);

  const emailWidget = useMemo(
    () =>
      page
        ? widgets.find(
            w =>
              w.pageId === dialogueId &&
              w.type === WidgetTypes.EMAIL &&
              w.id === data.widget.id,
          ) || page.widgets?.find(w => w.type === WidgetTypes.EMAIL)
        : undefined,
    [page, dialogueId, widgets, data.widget.id],
  );

  const isSoldEmailWidget = emailWidget?.soldConfigurations;

  const emailWidgetConfig = emailWidget?.config as unknown as EmailConfig;

  const commonWidgetHandlers = useCommonWidgetHandlers();

  const chatbotId = useSelector(activeChatbotIdSelector);

  const placeholder = config.sendNextAt ? 'Wait until' : 'Wait for';

  const time: ITime = useMemo(() => {
    return {
      date: config.sendNextAt,
      value: config.sendNextAfterValue,
      measure: config.sendNextAfterMeasure,
    };
  }, [
    config.sendNextAt,
    config.sendNextAfterValue,
    config.sendNextAfterMeasure,
  ]);

  const hasTime = !!Object.values(time).filter(Boolean).length;

  const firstMessage = useDialoguePreviewMessage({
    dialogueId,
    widgetId: emailWidget?.id,
  });

  const viewActions = useViewActions();

  const automationActions = useAutomationActions();

  const toggleReviewStatus = async () => {
    if (widget.needsReview) {
      try {
        await commonWidgetHandlers.updateField(widget, 'needsReview', false);
        automationActions.decreaseReviewRequiredCount();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const onClick = (fromAction?: IAutomationActionToOpenOnReady) => {
    console.debug(`Dialogue: onClick: `, { data, widget });
    if (isDragging) {
      return;
    }

    toggleReviewStatus();

    if (isEmail || fromAction?.id === EAutomationAction.SEND_EMAIL) {
      return viewActions.showSlideoutMenu('email', {
        chatbotId,
        navigateWidget: widget,
        dialogueId: widget.config.pageId,
      });
    }
    if (isSoldEmailWidget) {
      return viewActions.showSlideoutMenu('email', {
        title: (
          <div className={automationStyles.soldSlideoutTitle}>
            <SOLDIcon className={automationStyles.soldSlideoutTitleIcon} />
            <Heading size={4}>S.O.L.D. Email</Heading>
          </div>
        ),
        showSmartSendingInfo: true,
        chatbotId,
        dialogueId,
        widgetId: widget.id,
      });
    }

    viewActions.showSlideoutMenu('editor', {
      chatbotId,
      dialogueId,
    });
  };

  const onEmailStatsClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    console.debug(`Dialogue: onEmailStatsClick: `, { data, widget });

    if (isDragging) {
      return;
    }
    e.stopPropagation();

    return viewActions.showSlideoutMenu('emailStats', {
      chatbotId,
      dialogueId,
      emailWidgetId: emailWidget?.id,
    });
  };

  useOpenOnReady(widget, onClick);

  const onSubmit = async ({ value, measure, date }: ITime, closeModal) => {
    console.debug(`Dialogue: onSubmit: `, { value, measure });

    if (isDragging) {
      return;
    }

    try {
      await commonWidgetHandlers.update({
        ...widget,
        config: {
          ...widget.config,
          sendNextAt: date,
          sendNextAfterValue: value,
          sendNextAfterMeasure: measure,
        } as any,
      });
      closeModal();
    } catch (err) {}
  };

  const onDateClick = () => {
    console.debug(`Dialogue: onDateClick: `, { data, widget });
    viewActions.showSlideoutMenu('dateTime', {
      title: 'Wait',
      onSubmit,
      time: {
        date: config.sendNextAt,
        value: config.sendNextAfterValue,
        measure: config.sendNextAfterMeasure,
      },
    });
  };

  const visitedCount = page?.viewsCount;

  const widgetsCRUDActions = useWidgetCRUDActions();

  const showReviewBadge = emailWidget
    ? !widgets.find(w => w.id === emailWidget.id)?.validated.facebook ||
      widget.needsReview
    : widget.needsReview;

  const modalActions = useModalActions();

  useEffect(() => {
    if (isEmail && dialogueId && !emailWidget) {
      widgetsCRUDActions.retrieveAll(dialogueId);
    }
  }, []);

  return (
    <div className={css.wrapper}>
      {showReviewBadge && (
        <ReviewBadge onClick={() => onClick()} withIcon text="Needs review" />
      )}
      <Handle type="target" position={Position.Top} />
      <div
        className={classNames(className, common.card, css.container)}
        onClick={() => onClick()}
      >
        <div className={css.body}>
          <div className={css.header}>
            <div className={css.icon}>
              {!platform && !isSoldEmailWidget && <DialogueIcon />}
              {platform === 'sms' && <SmsIcon />}
              {isEmail && <EmailDialogueIcon />}
              {isSoldEmailWidget && <SOLDIcon />}
            </div>

            <div className={css.headerText}>
              <span className={css.placeholder}>
                {isSoldEmailWidget ? 'S.o.L.D. Email' : page?.name || '...'}
              </span>
              {(platform === 'sms' ||
                platform === 'email' ||
                isSoldEmailWidget) &&
                firstMessage && (
                  <span className={css.text}>{firstMessage}</span>
                )}
            </div>
          </div>

          {emailWidgetConfig ? (
            <div className={css.analyticsWrapper}>
              <div className={css.extendedAnalytics}>
                <div
                  className={css.analytics}
                  onClick={e => e.stopPropagation()}
                >
                  <Tooltip title="Total">
                    <div className={css.analyticsRow}>
                      <span className={css.analyticsIcon}>
                        <PeopleIcon
                          className={css.peopleIcon}
                          color="#595959"
                        />
                      </span>
                      <span className={css.analyticsText}>
                        <span className={css.analyticsValue}>
                          {numFormatter.int(emailWidgetConfig.emailTotalCount)}
                        </span>
                      </span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Sent">
                    <div className={css.analyticsRow}>
                      <span className={css.analyticsIcon}>
                        <SendOutlined color="#595959" />
                      </span>
                      <span className={css.analyticsText}>
                        <span className={css.analyticsValue}>
                          {numFormatter.int(emailWidgetConfig.emailSendsCount)}
                        </span>
                      </span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Opened">
                    <div className={css.analyticsRow}>
                      <span className={css.analyticsIcon}>
                        <MailIcon className={css.openedIcon} color="#595959" />
                      </span>
                      <span className={css.analyticsText}>
                        <span className={css.analyticsValue}>
                          {numFormatter.int(emailWidgetConfig.emailOpensCount)}
                        </span>
                      </span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Clicked">
                    <div className={css.analyticsRow}>
                      <span className={css.analyticsIcon}>
                        <ClickIcon className={css.clickIcon} color="#595959" />
                      </span>
                      <span className={css.analyticsText}>
                        <span className={css.analyticsValue}>
                          {numFormatter.int(emailWidgetConfig.emailClicksCount)}
                        </span>
                      </span>
                    </div>
                  </Tooltip>
                </div>
                <div
                  onClick={e => {
                    e.stopPropagation();
                    setExpandedAnalytics(!expandedAnalytics);
                  }}
                  className={classNames(css.analyticsText, css.expand)}
                >
                  {expandedAnalytics ? <UpOutlined /> : <DownOutlined />} Expand
                </div>
              </div>
              {expandedAnalytics && (
                <div
                  className={css.connectMore}
                  onClick={e => e.stopPropagation()}
                >
                  Send capacity is <strong>{sendCapacityValue}</strong>
                  <Tooltip
                    isWhite
                    title={
                      <div
                        className={automationStyles.connectMoreTooltipContent}
                      >
                        <span>
                          Based on the number of email accounts connected and
                          current leads you have a sending queue.
                        </span>
                        Connect more email accounts to increase your sending
                        speed.
                        <Button
                          onClick={e => {
                            e?.stopPropagation();
                            modalActions.openActivateEmailAccountModal();
                          }}
                        >
                          Connect emails
                        </Button>
                      </div>
                    }
                    placement="bottom"
                  >
                    <span className={css.analyticsIcon}>
                      <QuestionCircleOutlined
                        className={automationStyles.geekblue}
                      />
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
          ) : (
            <Count className={css.stat} value={`${visitedCount || 0} viewed`} />
          )}
          <div>
            {React.Children.map<ReactNode, ReactNode>(children, child => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                  // @ts-ignore onEdit does not seemto be present or types are off in @types/react
                  onEdit: () => onClick(),
                  key: uniqueId(),
                });
              }
            })}
          </div>
        </div>
      </div>

      {hasTime && (
        <Date
          className={css.date}
          placeholder={placeholder}
          awaitingCount={config?.delaysCount}
          time={time}
          onClick={onDateClick}
        />
      )}

      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};
